'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { LocalJSONStorage } from '~/utils/js';
import { useLocalStorageState } from './storage';
import { usePortalCtx } from '~/features/global/portal';

// Maybe this should be moved to a separate feature
export class HREF implements HREF.JSONValues {
    constructor(
        public pathname: HREF.Pathname,
        public search: HREF.Search | null,
        public hash: HREF.Hash | null,
    ) {}

    toString(): string {
        return `${this.pathname}${this.search}${this.hash}`;
    }
    static parseURL(url: Pick<URL, 'pathname' | 'search' | 'hash'>): HREF {
        return new HREF(
            url.pathname as HREF.Pathname,
            (url.search as HREF.Search) || null,
            (url.hash as HREF.Hash) || null,
        );
    }

    toJSON(): HREF.JSONValues {
        return {
            pathname: this.pathname,
            search: this.search,
            hash: this.hash,
        };
    }

    fromJSON(json: HREF.JSONValues): HREF {
        return new HREF(json.pathname, json.search, json.hash);
    }

    static eq(a: HREF.JSONValues, b: HREF.JSONValues): boolean {
        return a.pathname === b.pathname && a.search === b.search && a.hash === b.hash;
    }
    eq(rhs: HREF.JSONValues): boolean {
        return HREF.eq(this, rhs);
    }
}
export declare namespace HREF {
    export type Pathname = `/${string}`;
    export type Search = `?${string}`;
    export type Hash = `#${string}`;

    export interface JSONValues {
        pathname: HREF.Pathname;
        search: HREF.Search | null;
        hash: HREF.Hash | null;
    }
}

declare namespace HISTORY {
    /** A "special" page on which visits needs to be tracked for certain features (e.g. back button on PDP) */
    export type SpecialPage = 'PDP.backToOverview';
}
/** Custom history SDK to track certain page views */
export const HISTORY = {
    specialPages: new LocalJSONStorage<{
        [Page in HISTORY.SpecialPage]?: HREF.JSONValues & {
            portal: usePortalCtx.Portal;
        };
    }>('history:specialPages', {}, 'sessionStorage'),

    /** Previous page. Only gets set on navigate */
    previous: new LocalJSONStorage<null | HREF.JSONValues>(
        'history:previous',
        null,
        'sessionStorage',
    ),
};

/** NOTE: this needs to wrapped in Suspense or it will cause it's page to be dynamic */
export const Action_History_TrackGlobals: React.FC = _ => {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const previousPage = useLocalStorageState(HISTORY.previous);

    useEffect(() => {
        // Track the previous page
        return () => {
            const newHREF = new HREF(
                pathname as HREF.Pathname,
                searchParams.size ? `?${decodeURIComponent(searchParams.toString())}` : null,
                null,
            );

            // console.debug('history:previous:setState', newHREF, previousPage.state);
            if (
                (previousPage.state && newHREF.eq(previousPage.state)) ||
                newHREF.eq({
                    pathname: window.location.pathname as HREF.Pathname,
                    search: window.location.search as HREF.Search,
                    hash: null,
                })
            ) {
                // Don't set current URL
                return;
            }

            // console.debug('history:previous:setState', 'DONE');

            previousPage.setState(newHREF.toJSON());
        };
    }, [pathname, searchParams]);

    return null;
};
